import { useState, useEffect, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useCrossFacilityMonitoringLocations,
  useMapMonitoringLocations,
} from "@/hooks/use-monitoring-locations";
import { useFacilities, useFacility } from "@/hooks/use-facilities";
import { useCrossFacilityColumns } from "@/components/monitoring-locations/cross-facility-columns";
import {
  LocationFilters,
  type LocationFilterValues,
} from "@/components/monitoring-locations/location-filters";
import { CollapsibleMapPanel } from "@/components/monitoring-locations/collapsible-map-panel";
import { type ViewportBounds } from "@/components/monitoring-locations/leaflet-map";
import { type CrossFacilityItem } from "@/components/monitoring-locations/constants";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  Breadcrumbs,
  Combobox,
} from "@/components/ui";

interface SearchParams {
  facilityId?: string;
}

export const Route = createFileRoute("/_authenticated/monitoring-locations/")({
  component: CrossFacilityMonitoringLocationsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    facilityId: typeof search.facilityId === "string" ? search.facilityId : undefined,
  }),
});

const PAGE_SIZE = 20;
const MAP_COLLAPSED_KEY = "ml-map-collapsed";

function getMapCollapsed(): boolean {
  try {
    return localStorage.getItem(MAP_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function CrossFacilityMonitoringLocationsPage() {
  const { t } = useTranslation(["monitoringLocations", "common", "org"]);
  const navigate = useNavigate();
  const { facilityId: urlFacilityId } = Route.useSearch();

  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canCreate = can("monitoring_locations", "create");

  const [selectedFacilityId, setSelectedFacilityId] = useState(urlFacilityId ?? "");
  const [facilitySearch, setFacilitySearch] = useState("");
  const [filters, setFilters] = useState<LocationFilterValues>({});
  const [offset, setOffset] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "facility_name", desc: false }]);
  const [isMapCollapsed, setIsMapCollapsed] = useState(getMapCollapsed);
  const [viewportBounds, setViewportBounds] = useState<ViewportBounds | null>(null);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  // Reset offset when sorting changes
  useEffect(() => {
    setOffset(0);
  }, [sorting]);

  const columns = useCrossFacilityColumns();

  const { data: facilitiesData, isFetching: facilitiesFetching } = useFacilities(orgId, {
    limit: 20,
    status: "active",
    search: facilitySearch || undefined,
  });
  const facilities = facilitiesData?.data ?? [];

  // Resolve the selected facility name when it might not be in the search results
  const { data: selectedFacility } = useFacility(orgId, selectedFacilityId);
  const selectedFacilityLabel = selectedFacilityId
    ? (facilities.find((f) => f.id === selectedFacilityId)?.name ?? selectedFacility?.name)
    : undefined;

  // Shared attribute filters (no pagination, no viewport)
  const attributeFilters = {
    facility_id: selectedFacilityId || undefined,
    ...filters,
  };

  // Stable filter key for fitBounds control
  const filterKey = [
    selectedFacilityId,
    filters.location_type ?? "",
    filters.status ?? "",
    filters.search ?? "",
  ].join("|");

  // Map query: all locations (no pagination), view=map
  const { data: mapData } = useMapMonitoringLocations(orgId, attributeFilters);
  const mapLocations = (mapData?.data ?? []) as CrossFacilityItem[];

  // Table query: paginated, optionally viewport filtered
  const { data, isLoading, isError, error } = useCrossFacilityMonitoringLocations(orgId, {
    ...attributeFilters,
    limit: PAGE_SIZE,
    offset,
    sort_by: sortBy,
    sort_dir: sortDir,
    ...(viewportBounds
      ? {
          lat_min: viewportBounds.latMin,
          lat_max: viewportBounds.latMax,
          lng_min: viewportBounds.lngMin,
          lng_max: viewportBounds.lngMax,
        }
      : {}),
  });

  const locations = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleToggleCollapsed = useCallback(() => {
    setIsMapCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(MAP_COLLAPSED_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const handleSearchArea = useCallback((bounds: ViewportBounds) => {
    setViewportBounds(bounds);
    setOffset(0);
  }, []);

  const handleClearViewport = useCallback(() => {
    setViewportBounds(null);
    setOffset(0);
  }, []);

  const handleRowClick = (row: CrossFacilityItem) => {
    void navigate({
      to: "/monitoring-locations/$locationId",
      params: { locationId: row.id },
      search: { facilityId: row.facility_id },
    });
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("title") }]} />
      <PageHeader
        title={t("title")}
        subtitle={t("facilities.totalCount", { count: total })}
        actions={
          canCreate ? (
            selectedFacilityId ? (
              <Button
                onClick={() =>
                  void navigate({
                    to: "/monitoring-locations/new",
                    search: { facilityId: selectedFacilityId },
                  })
                }
              >
                {t("addLocation")}
              </Button>
            ) : (
              <Button disabled title={t("selectFacilityToCreate")}>
                {t("addLocation")}
              </Button>
            )
          ) : undefined
        }
      />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Combobox
          className="w-64 shrink-0"
          value={selectedFacilityId}
          onChange={(val) => {
            setSelectedFacilityId(val);
            setOffset(0);
          }}
          onSearch={setFacilitySearch}
          loading={facilitiesFetching}
          placeholder={t("allFacilities")}
          options={facilities.map((f) => ({ value: f.id, label: f.name }))}
          selectedLabel={selectedFacilityLabel}
        />
        <LocationFilters
          filters={filters}
          onChange={(f) => {
            setFilters(f);
            setOffset(0);
          }}
        />
      </div>

      <CollapsibleMapPanel
        mapLocations={mapLocations}
        onSearchArea={handleSearchArea}
        filterKey={filterKey}
        isCollapsed={isMapCollapsed}
        onToggleCollapsed={handleToggleCollapsed}
      />

      {viewportBounds && (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted">
          <span>
            {t("showingInView", {
              count: total,
              total: mapLocations.length,
            })}
          </span>
          <Button variant="secondary" size="sm" onClick={handleClearViewport}>
            {t("showAll")}
          </Button>
        </div>
      )}

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {t("facilities.loadFailed")}: {error instanceof Error ? error.message : t("unknownError")}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={7} rows={5} />
        </div>
      ) : locations.length === 0 ? (
        <div className="mt-6">
          <EmptyState title={t("noLocations")} description={t("noLocationsDescription")} />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={locations}
              onRowClick={handleRowClick}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
